import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Bike, Plus, X, Check, Clock, AlertTriangle, Download, 
  Camera, Upload, ChevronRight, ChevronLeft, Search,
  Phone, Mail, FileText, Calendar, User, CreditCard,
  Play, Square, RotateCcw, TrendingUp, QrCode, Edit, Trash2, ScanLine, Eye
} from 'lucide-react';
import { testFleetAPI, testRidesAPI, API_BASE } from '../../api';
import QRScanner from '../../components/QRScanner';

// ============================================================
// CONSTANTS
// ============================================================

const PERMIT_CATEGORIES = ['A', 'A1', 'A2', 'B'];
const DURATIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 heure' },
  { value: 120, label: '2 heures' },
];

const BADGE_COLORS = [
  { value: '#3b82f6', label: 'Bleu' },
  { value: '#22c55e', label: 'Vert' },
  { value: '#f59e0b', label: 'Orange' },
  { value: '#ef4444', label: 'Rouge' },
  { value: '#8b5cf6', label: 'Violet' },
  { value: '#ec4899', label: 'Rose' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#84cc16', label: 'Lime' },
];

const STATUT_CONFIG = {
  en_attente: { label: 'En attente', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  rdv_planifie: { label: 'RDV Planifié', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)' },
  en_cours: { label: 'En cours', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },
  termine: { label: 'Terminé', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
  annule: { label: 'Annulé', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)' },
};

const DECHARGE_TEXT = (nom, categorie) => `
DÉCHARGE DE RESPONSABILITÉ - ESSAI MOTO

Je soussigné(e), ${nom || '[NOM DU CLIENT]'}, certifie sur l'honneur :

1. Être titulaire d'un permis de conduire valide de catégorie ${categorie || '[CATÉGORIE]'} me permettant de conduire le véhicule mis à disposition.

2. Être en état physique et mental approprié pour conduire un véhicule motorisé.

3. M'engager à respecter le code de la route et les limitations de vitesse en vigueur.

4. Accepter d'être tenu(e) responsable de tout dommage causé au véhicule durant l'essai, à l'exception de l'usure normale.

5. Reconnaître avoir été informé(e) des caractéristiques techniques du véhicule et de son fonctionnement.

6. M'engager à ne pas prêter le véhicule à un tiers durant l'essai.

7. Accepter que No Name Motorcycles puisse annuler l'essai à tout moment pour des raisons de sécurité.

8. FRANCHISE : En cas de dégât ou d'accident survenant durant l'essai, j'accepte de prendre en charge une franchise de CHF 1'500.- (mille cinq cents francs suisses) correspondant aux frais de réparation ou de remplacement du véhicule.

En signant ce document, je décharge No Name Motorcycles SA, ses employés et représentants de toute responsabilité en cas d'accident survenant durant l'essai, dans les limites prévues par la loi.

Fait à Bevaix, le ${new Date().toLocaleDateString('fr-CH')}
`;

// ============================================================
// SIGNATURE CANVAS COMPONENT
// ============================================================

const SignatureCanvas = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#18181b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#18181b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onClear?.();
  };

  const saveSignature = () => {
    if (!hasSignature) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave?.(dataUrl);
  };

  return (
    <div className="space-y-3">
      <div className="border border-[var(--border)] rounded-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          width={500}
          height={200}
          className="w-full touch-none cursor-crosshair"
          style={{ background: '#18181b' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex gap-2">
        <button 
          type="button" 
          className="btn btn-ghost flex-1"
          onClick={clearCanvas}
        >
          <RotateCcw size={14} /> Effacer
        </button>
        <button 
          type="button" 
          className="btn btn-primary flex-1"
          onClick={saveSignature}
          disabled={!hasSignature}
        >
          <Check size={14} /> Valider signature
        </button>
      </div>
    </div>
  );
};

// ============================================================
// CHRONOMETRE COMPONENT
// ============================================================

const Chronometre = ({ startTime }) => {
  const [elapsed, setElapsed] = useState('00:00');

  useEffect(() => {
    if (!startTime) return;
    
    const updateElapsed = () => {
      const start = new Date(startTime);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const secs = diff % 60;
      
      if (hours > 0) {
        setElapsed(`${hours}h${minutes.toString().padStart(2, '0')}`);
      } else {
        setElapsed(`${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
      }
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <span className="font-mono text-lg font-bold text-[var(--accent)]">{elapsed}</span>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const TestRides = () => {
  // State
  const [activeTab, setActiveTab] = useState('essais');
  const [rides, setRides] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  // Modals
  const [showNewRideModal, setShowNewRideModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showFleetModal, setShowFleetModal] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedBike, setSelectedBike] = useState(null);
  
  // Form state
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1 - Prospect
    prenom: '',
    nom: '',
    telephone: '',
    email: '',
    adresse: '',
    type_essai: 'walk_in',
    date_rdv: '',
    heure_rdv: '',
    duree_prevue: 30,
    // Step 2 - Moto & Permis
    bike_id: '',
    bike_code: '',
    categorie_permis: 'A',
    photo_permis_path: null,
    notes_vendeur: '',
    // Step 3 - Consentements
    decharge_acceptee: false,
    signature_path: null,
    consentement_marketing: false,
  });
  
  // Autocomplete for previous clients
  const [prospectSuggestions, setProspectSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showPermitImage, setShowPermitImage] = useState(false);
  
  // Return modal state
  const [returnScan, setReturnScan] = useState('');
  const [returnError, setReturnError] = useState('');
  const [returnSuccess, setReturnSuccess] = useState(false);

  // QR Scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScanContext, setQrScanContext] = useState(null); // 'bike_select', 'return_scan'

  // Fleet form state
  const [fleetForm, setFleetForm] = useState({
    marque: '',
    modele: '',
    annee: new Date().getFullYear(),
    immatriculation: '',
    couleur_badge: '#3b82f6',
    km_actuel: 0,
    type_moto: 'neuf',
    notes: '',
  });

  // ============================================================
  // DATA FETCHING
  // ============================================================

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ridesRes, fleetRes, statsRes] = await Promise.all([
        testRidesAPI.getAll(),
        testFleetAPI.getAll(),
        testRidesAPI.getStats(),
      ]);
      setRides(ridesRes.data);
      setFleet(fleetRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching test rides data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh stats every 30 seconds for chronometer
    const interval = setInterval(() => {
      testRidesAPI.getStats().then(res => setStats(res.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const getBike = (bikeId) => fleet.find(b => b.id === bikeId);

  // QR Scanner handlers
  const openQRScanner = (context) => {
    setQrScanContext(context);
    setShowQRScanner(true);
  };

  const handleQRScan = async (code) => {
    setShowQRScanner(false);
    
    if (qrScanContext === 'bike_select') {
      // Scanning for bike selection in new ride form
      try {
        const res = await testFleetAPI.resolveBikeCode(code);
        setFormData({ ...formData, bike_id: res.data.id, bike_code: code });
      } catch (err) {
        alert(`Moto non trouvée avec le code: ${code}`);
      }
    } else if (qrScanContext === 'return_scan') {
      // Scanning for return verification
      setReturnScan(code);
      // Auto-verify
      const bike = getBike(selectedRide?.bike_id);
      const scanUpper = code.toUpperCase();
      if (scanUpper === bike?.qr_code?.toUpperCase() || scanUpper === bike?.immatriculation?.toUpperCase()) {
        setReturnError('');
        setReturnSuccess(true);
      } else {
        setReturnError(`Le scan "${code}" ne correspond pas à la moto partie (${bike?.marque} ${bike?.modele} - ${bike?.immatriculation})`);
        setReturnSuccess(false);
      }
    }
    
    setQrScanContext(null);
  };

  const handleBikeCodeSearch = async () => {
    if (!formData.bike_code) return;
    try {
      const res = await testFleetAPI.resolveBikeCode(formData.bike_code);
      setFormData({ ...formData, bike_id: res.data.id });
    } catch (err) {
      alert('Moto non trouvée avec ce code');
    }
  };

  const handlePermitUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const res = await testRidesAPI.uploadPermit(formDataUpload);
      setFormData({ ...formData, photo_permis_path: res.data.path });
    } catch (err) {
      alert('Erreur upload photo permis');
    }
  };

  const handleSignatureSave = async (dataUrl) => {
    try {
      const res = await testRidesAPI.uploadSignature(dataUrl);
      setFormData({ ...formData, signature_path: res.data.path });
    } catch (err) {
      alert('Erreur sauvegarde signature');
    }
  };

  const handleCreateRide = async () => {
    if (!formData.bike_id || !formData.decharge_acceptee || !formData.signature_path) {
      alert('Veuillez compléter tous les champs obligatoires');
      return;
    }
    
    try {
      const payload = {
        prospect: {
          prenom: formData.prenom,
          nom: formData.nom,
          telephone: formData.telephone,
          email: formData.email,
          adresse: formData.adresse,
        },
        bike_id: formData.bike_id,
        bike_scan_depart: formData.bike_code || null,
        categorie_permis: formData.categorie_permis,
        photo_permis_path: formData.photo_permis_path,
        notes_vendeur: formData.notes_vendeur,
        decharge_acceptee: formData.decharge_acceptee,
        signature_path: formData.signature_path,
        consentement_marketing: formData.consentement_marketing,
        type_essai: formData.type_essai,
        date_rdv: formData.type_essai === 'rdv_planifie' ? formData.date_rdv : null,
        heure_rdv: formData.type_essai === 'rdv_planifie' ? formData.heure_rdv : null,
        duree_prevue: formData.duree_prevue,
      };
      
      await testRidesAPI.create(payload);
      setShowNewRideModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      // Afficher le message d'erreur détaillé du backend
      const errorMessage = err.response?.data?.detail || 'Erreur création essai';
      alert(errorMessage);
      console.error(err);
    }
  };

  const handleStartRide = async (rideId) => {
    try {
      await testRidesAPI.start(rideId);
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erreur démarrage essai';
      alert(errorMessage);
    }
  };

  const handleCancelRide = async (rideId) => {
    if (!confirm('Annuler cet essai ?')) return;
    try {
      await testRidesAPI.cancel(rideId);
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erreur annulation essai';
      alert(errorMessage);
    }
  };

  const handleOpenReturn = (ride) => {
    setSelectedRide(ride);
    setReturnScan('');
    setReturnError('');
    setReturnSuccess(false);
    setShowReturnModal(true);
  };

  const handleVerifyReturn = async () => {
    if (!returnScan) return;
    
    const bike = getBike(selectedRide.bike_id);
    const scanUpper = returnScan.toUpperCase();
    
    if (scanUpper !== bike?.qr_code?.toUpperCase() && scanUpper !== bike?.immatriculation?.toUpperCase()) {
      setReturnError(`Le scan "${returnScan}" ne correspond pas à la moto partie (${bike?.marque} ${bike?.modele} - ${bike?.immatriculation})`);
      setReturnSuccess(false);
    } else {
      setReturnError('');
      setReturnSuccess(true);
    }
  };

  const handleConfirmReturn = async () => {
    if (!returnSuccess) return;
    
    try {
      await testRidesAPI.returnBike(selectedRide.id, returnScan);
      setShowReturnModal(false);
      setSelectedRide(null);
      fetchData();
    } catch (err) {
      alert('Erreur validation retour');
    }
  };

  const handleConvertToLead = async (rideId) => {
    if (!window.confirm('Convertir cet essai en lead dans le pipeline de vente ?')) return;
    
    try {
      await testRidesAPI.convertToLead(rideId);
      fetchData();
      alert('Lead créé avec succès !');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur conversion');
    }
  };

  const handleExportCSV = async (rideIds = null) => {
    try {
      const res = await testRidesAPI.exportCSV(rideIds);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'essais_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Erreur export CSV');
    }
  };

  // Fleet handlers
  const handleCreateBike = async () => {
    try {
      await testFleetAPI.create(fleetForm);
      setShowFleetModal(false);
      setSelectedBike(null);
      resetFleetForm();
      fetchData();
    } catch (err) {
      alert('Erreur création moto');
    }
  };

  const handleUpdateBike = async () => {
    try {
      await testFleetAPI.update(selectedBike.id, fleetForm);
      setShowFleetModal(false);
      setSelectedBike(null);
      resetFleetForm();
      fetchData();
    } catch (err) {
      alert('Erreur modification moto');
    }
  };

  const handleDeleteBike = async (bikeId) => {
    if (!window.confirm('Désactiver cette moto de la flotte ?')) return;
    try {
      await testFleetAPI.delete(bikeId);
      fetchData();
    } catch (err) {
      alert('Erreur désactivation moto');
    }
  };

  const resetForm = () => {
    setFormStep(1);
    setFormData({
      prenom: '', nom: '', telephone: '', email: '', adresse: '',
      type_essai: 'walk_in', date_rdv: '', heure_rdv: '', duree_prevue: 30,
      bike_id: '', bike_code: '', categorie_permis: 'A',
      photo_permis_path: null, notes_vendeur: '',
      decharge_acceptee: false, signature_path: null, consentement_marketing: false,
    });
    setShowSuggestions(false);
    setProspectSuggestions([]);
  };

  // Autocomplete - search for existing clients
  const handleNameChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    if (value.length >= 2) {
      // Search in previous rides for matching prospects
      const searchTerm = value.toLowerCase();
      const matches = rides
        .filter(r => r.prospect && (
          r.prospect.prenom?.toLowerCase().includes(searchTerm) ||
          r.prospect.nom?.toLowerCase().includes(searchTerm)
        ))
        .map(r => r.prospect)
        .filter((p, i, arr) => arr.findIndex(x => x.email === p.email) === i) // Unique by email
        .slice(0, 5);
      setProspectSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectProspect = (prospect) => {
    setFormData({
      ...formData,
      prenom: prospect.prenom || '',
      nom: prospect.nom || '',
      telephone: prospect.telephone || '',
      email: prospect.email || '',
      adresse: prospect.adresse || '',
    });
    setShowSuggestions(false);
  };

  const resetFleetForm = () => {
    setFleetForm({
      marque: '', modele: '', annee: new Date().getFullYear(),
      immatriculation: '', couleur_badge: '#3b82f6', km_actuel: 0,
      type_moto: 'neuf', notes: '',
    });
  };

  const openEditBike = (bike) => {
    setSelectedBike(bike);
    setFleetForm({
      marque: bike.marque,
      modele: bike.modele,
      annee: bike.annee,
      immatriculation: bike.immatriculation,
      couleur_badge: bike.couleur_badge,
      km_actuel: bike.km_actuel || 0,
      type_moto: bike.type_moto || 'neuf',
      notes: bike.notes || '',
    });
    setShowFleetModal(true);
  };

  // Filter rides
  const filteredRides = rides.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'en_attente') return r.statut === 'en_attente';
    if (filter === 'en_cours') return r.statut === 'en_cours';
    if (filter === 'rdv') return r.statut === 'rdv_planifie';
    if (filter === 'termine') return r.statut === 'termine';
    return true;
  });

  // ============================================================
  // RENDER
  // ============================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent)]" />
      </div>
    );
  }

  return (
    <div data-testid="test-rides-view">
      {/* Tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'essais' 
                ? 'bg-[var(--accent)] text-white' 
                : 'bg-[var(--bg3)] text-[var(--text2)] hover:text-white'
            }`}
            onClick={() => setActiveTab('essais')}
            data-testid="tab-essais"
          >
            <Bike size={16} className="inline mr-2" />
            Essais
          </button>
          <button
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'flotte' 
                ? 'bg-[var(--accent)] text-white' 
                : 'bg-[var(--bg3)] text-[var(--text2)] hover:text-white'
            }`}
            onClick={() => setActiveTab('flotte')}
            data-testid="tab-flotte"
          >
            <QrCode size={16} className="inline mr-2" />
            Flotte ({fleet.length})
          </button>
        </div>
        
        {activeTab === 'essais' ? (
          <button 
            className="btn btn-primary"
            onClick={() => setShowNewRideModal(true)}
            data-testid="new-ride-btn"
          >
            <Plus size={14} /> Nouvel essai
          </button>
        ) : (
          <button 
            className="btn btn-primary"
            onClick={() => { setSelectedBike(null); resetFleetForm(); setShowFleetModal(true); }}
            data-testid="new-bike-btn"
          >
            <Plus size={14} /> Ajouter moto
          </button>
        )}
      </div>

      {/* ============================================================ */}
      {/* ESSAIS TAB */}
      {/* ============================================================ */}
      {activeTab === 'essais' && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="stat-card" data-testid="kpi-en-cours">
              <div className="stat-label">En essai</div>
              <div className="stat-value" style={{ color: 'var(--accent)' }}>{stats?.en_cours || 0}</div>
              <div className="stat-sub">actuellement</div>
            </div>
            <div className="stat-card" data-testid="kpi-rdv">
              <div className="stat-label">RDV Planifiés</div>
              <div className="stat-value" style={{ color: '#3b82f6' }}>{stats?.rdv_planifies || 0}</div>
              <div className="stat-sub">à venir</div>
            </div>
            <div className="stat-card" data-testid="kpi-termines">
              <div className="stat-label">Terminés aujourd'hui</div>
              <div className="stat-value" style={{ color: '#22c55e' }}>{stats?.termines_aujourd_hui || 0}</div>
              <div className="stat-sub">essais</div>
            </div>
            <div className="stat-card" data-testid="kpi-attente">
              <div className="stat-label">En attente</div>
              <div className="stat-value" style={{ color: '#f59e0b' }}>{stats?.en_attente || 0}</div>
              <div className="stat-sub">file d'attente</div>
            </div>
          </div>

          {/* FILE D'ATTENTE - Essais prêts au départ */}
          {stats?.rides_en_attente?.length > 0 && (
            <div className="card mb-6" style={{ borderColor: 'var(--yellow)', borderWidth: '2px' }}>
              <div className="card-header" style={{ background: 'rgba(245, 158, 11, 0.1)' }}>
                <span className="card-title flex items-center gap-2">
                  <Clock size={16} className="text-[var(--yellow)]" />
                  🎯 File d'attente - Prêts au départ ({stats.rides_en_attente.length})
                </span>
              </div>
              <div className="card-body p-0">
                {stats.rides_en_attente.map((ride, index) => {
                  const bike = getBike(ride.bike_id);
                  // Vérifier si la plaque est disponible
                  const isPlateInUse = stats?.rides_en_cours?.some(r => {
                    const rBike = getBike(r.bike_id);
                    return rBike?.immatriculation?.toUpperCase() === bike?.immatriculation?.toUpperCase();
                  });
                  
                  return (
                    <div 
                      key={ride.id}
                      className="flex items-center justify-between p-4 border-b border-[var(--border)] last:border-b-0"
                      style={{ background: isPlateInUse ? 'rgba(239, 68, 68, 0.05)' : 'transparent' }}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ background: isPlateInUse ? '#ef4444' : '#f59e0b' }}
                        >
                          {index + 1}
                        </div>
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ background: bike?.couleur_badge || '#3b82f6' }}
                        >
                          <Bike size={20} className="text-white" />
                        </div>
                        <div>
                          <div className="font-bold">{ride.prospect?.prenom} {ride.prospect?.nom}</div>
                          <div className="text-sm text-[var(--text3)]">
                            {bike?.marque} {bike?.modele} • {bike?.immatriculation}
                          </div>
                          {isPlateInUse && (
                            <div className="text-xs text-[var(--red)] mt-1">
                              ⏳ Plaque {bike?.immatriculation} en essai - attendre retour
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isPlateInUse ? (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleStartRide(ride.id)}
                            data-testid={`start-btn-${ride.id}`}
                          >
                            <Play size={14} /> Départ
                          </button>
                        ) : (
                          <span className="text-xs text-[var(--text3)] px-3 py-1 bg-[var(--bg3)] rounded">
                            🚫 Moto indisponible
                          </span>
                        )}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleCancelRide(ride.id)}
                          title="Annuler"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Essais en cours avec chronomètre */}
          {stats?.rides_en_cours?.length > 0 && (
            <div className="card mb-6">
              <div className="card-header">
                <span className="card-title flex items-center gap-2">
                  <Clock size={16} className="text-[var(--accent)]" />
                  🏍️ Essais en cours ({stats.rides_en_cours.length})
                </span>
              </div>
              <div className="card-body p-0">
                {stats.rides_en_cours.map(ride => {
                  const bike = getBike(ride.bike_id);
                  return (
                    <div 
                      key={ride.id}
                      className="flex items-center justify-between p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg3)]"
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ background: bike?.couleur_badge || '#3b82f6' }}
                        >
                          <Bike size={20} className="text-white" />
                        </div>
                        <div>
                          <div className="font-bold">{ride.prospect?.prenom} {ride.prospect?.nom}</div>
                          <div className="text-sm text-[var(--text3)]">
                            {bike?.marque} {bike?.modele} • {bike?.immatriculation}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Chronometre startTime={ride.heure_depart} />
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => handleOpenReturn(ride)}
                          data-testid={`return-btn-${ride.id}`}
                        >
                          <Square size={14} /> Valider retour
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filters & Actions */}
          <div className="flex items-center justify-between mb-4">
            <div className="filter-tabs">
              <button 
                className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                Tous ({rides.length})
              </button>
              <button 
                className={`filter-tab ${filter === 'en_attente' ? 'active' : ''}`}
                onClick={() => setFilter('en_attente')}
                style={filter === 'en_attente' ? { background: '#f59e0b' } : {}}
              >
                En attente
              </button>
              <button 
                className={`filter-tab ${filter === 'en_cours' ? 'active' : ''}`}
                onClick={() => setFilter('en_cours')}
              >
                En cours
              </button>
              <button 
                className={`filter-tab ${filter === 'rdv' ? 'active' : ''}`}
                onClick={() => setFilter('rdv')}
              >
                RDV
              </button>
              <button 
                className={`filter-tab ${filter === 'termine' ? 'active' : ''}`}
                onClick={() => setFilter('termine')}
              >
                Terminés
              </button>
            </div>
            
            <button 
              className="btn btn-ghost"
              onClick={() => handleExportCSV()}
              data-testid="export-csv-btn"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>

          {/* Rides List */}
          <div className="space-y-3">
            {filteredRides.map(ride => {
              const bike = getBike(ride.bike_id);
              const statutConfig = STATUT_CONFIG[ride.statut] || STATUT_CONFIG.rdv_planifie;
              
              return (
                <div 
                  key={ride.id}
                  className="card p-4 hover:border-[var(--accent)] transition-colors cursor-pointer"
                  data-testid={`ride-card-${ride.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{ background: bike?.couleur_badge || '#3b82f6' }}
                      >
                        <Bike size={24} className="text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-lg">
                            {ride.prospect?.prenom} {ride.prospect?.nom}
                          </span>
                          <span 
                            className="badge"
                            style={{ background: statutConfig.bg, color: statutConfig.color }}
                          >
                            {statutConfig.label}
                          </span>
                          {ride.consentement_marketing && (
                            <span className="badge badge-green" title="Consentement marketing">
                              RGPD
                            </span>
                          )}
                          {ride.photo_permis_path && (
                            <span className="text-xs text-[var(--text3)]" title="Photo permis">
                              📷
                            </span>
                          )}
                          {ride.signature_path && (
                            <span className="text-xs text-[var(--text3)]" title="Signé">
                              ✍
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-[var(--text2)] mb-1">
                          {bike?.marque} {bike?.modele} • {bike?.immatriculation}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[var(--text3)]">
                          <span><Phone size={12} className="inline mr-1" />{ride.prospect?.telephone}</span>
                          <span><Mail size={12} className="inline mr-1" />{ride.prospect?.email}</span>
                          <span><CreditCard size={12} className="inline mr-1" />Permis {ride.categorie_permis}</span>
                        </div>
                        {ride.prospect?.adresse && (
                          <div className="text-xs text-[var(--text3)] mt-1">
                            📍 {ride.prospect.adresse}
                          </div>
                        )}
                        {ride.photo_permis_path && (
                          <button 
                            className="text-xs text-[var(--accent)] mt-1 flex items-center gap-1 hover:underline"
                            onClick={() => window.open(`${API_BASE.replace('/api', '')}${ride.photo_permis_path}`, '_blank')}
                          >
                            <Eye size={12} /> Voir le permis
                          </button>
                        )}
                        {ride.notes_vendeur && (
                          <div className="text-xs text-[var(--text3)] mt-2 italic">
                            "{ride.notes_vendeur}"
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-xs text-[var(--text3)]">
                        {ride.type_essai === 'rdv_planifie' && ride.date_rdv ? (
                          <span>{ride.date_rdv} à {ride.heure_rdv}</span>
                        ) : ride.heure_depart ? (
                          <span>Départ: {new Date(ride.heure_depart).toLocaleString('fr-CH')}</span>
                        ) : (
                          <span>{new Date(ride.created_at).toLocaleDateString('fr-CH')}</span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {ride.statut === 'rdv_planifie' && (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleStartRide(ride.id)}
                            data-testid={`start-btn-${ride.id}`}
                          >
                            <Play size={12} /> Démarrer
                          </button>
                        )}
                        {ride.statut === 'en_cours' && (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleOpenReturn(ride)}
                          >
                            <Square size={12} /> Retour
                          </button>
                        )}
                        {ride.statut === 'termine' && !ride.converti_en_lead && (
                          <button 
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleConvertToLead(ride.id)}
                            data-testid={`convert-btn-${ride.id}`}
                          >
                            <TrendingUp size={12} /> Créer lead
                          </button>
                        )}
                        {ride.converti_en_lead && (
                          <span className="badge badge-green">Lead créé</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {filteredRides.length === 0 && (
              <div className="text-center py-12 text-[var(--text3)]">
                Aucun essai trouvé
              </div>
            )}
          </div>
        </>
      )}

      {/* ============================================================ */}
      {/* FLOTTE TAB */}
      {/* ============================================================ */}
      {activeTab === 'flotte' && (
        <div className="grid grid-cols-3 gap-4">
          {fleet.map(bike => (
            <div 
              key={bike.id}
              className="card p-4 hover:border-[var(--accent)] transition-colors"
              data-testid={`fleet-card-${bike.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ background: bike.couleur_badge }}
                >
                  <Bike size={24} className="text-white" />
                </div>
                <div className="flex gap-1">
                  <button 
                    className="p-2 rounded hover:bg-[var(--bg3)]"
                    onClick={() => openEditBike(bike)}
                    data-testid={`edit-bike-${bike.id}`}
                  >
                    <Edit size={14} className="text-[var(--text3)]" />
                  </button>
                  <button 
                    className="p-2 rounded hover:bg-[var(--bg3)]"
                    onClick={() => handleDeleteBike(bike.id)}
                    data-testid={`delete-bike-${bike.id}`}
                  >
                    <Trash2 size={14} className="text-[var(--text3)]" />
                  </button>
                </div>
              </div>
              
              <div className="font-bold text-lg mb-1">{bike.marque} {bike.modele}</div>
              <div className="text-sm text-[var(--text2)] mb-2">{bike.annee}</div>
              
              <div className="space-y-1 text-xs text-[var(--text3)]">
                <div className="flex justify-between">
                  <span>Immatriculation</span>
                  <span className="font-mono font-bold text-[var(--text)]">{bike.immatriculation}</span>
                </div>
                <div className="flex justify-between">
                  <span>QR Code</span>
                  <span className="font-mono text-[var(--accent)]">{bike.qr_code}</span>
                </div>
                <div className="flex justify-between">
                  <span>Type</span>
                  <span className={`badge ${bike.type_moto === 'occasion' ? 'badge-yellow' : 'badge-blue'}`}>
                    {bike.type_moto === 'occasion' ? 'Occasion' : 'Neuf'}
                  </span>
                </div>
                {bike.km_actuel > 0 && (
                  <div className="flex justify-between">
                    <span>Kilométrage</span>
                    <span>{bike.km_actuel.toLocaleString()} km</span>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {fleet.length === 0 && (
            <div className="col-span-3 text-center py-12 text-[var(--text3)]">
              Aucune moto dans la flotte d'essai
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* NEW RIDE MODAL - 3 STEPS */}
      {/* ============================================================ */}
      {showNewRideModal && (
        <div className="panel-overlay" onClick={() => { setShowNewRideModal(false); resetForm(); }}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '600px' }}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="panel-title">Nouvel essai</div>
                <div className="text-sm text-[var(--text3)] mt-1">
                  Étape {formStep}/3 — {formStep === 1 ? 'Informations prospect' : formStep === 2 ? 'Moto & Permis' : 'Décharge & Signature'}
                </div>
              </div>
              <button className="panel-close" onClick={() => { setShowNewRideModal(false); resetForm(); }}>
                <X size={20} />
              </button>
            </div>

            {/* Progress bar */}
            <div className="flex gap-2 mb-6">
              {[1, 2, 3].map(step => (
                <div 
                  key={step}
                  className="h-1 flex-1 rounded-full transition-all"
                  style={{ 
                    background: step <= formStep ? 'var(--accent)' : 'var(--border2)'
                  }}
                />
              ))}
            </div>

            {/* Step 1: Prospect Info */}
            {formStep === 1 && (
              <div className="space-y-4">
                <div className="grid-2 gap-4">
                  <div className="relative">
                    <label className="block text-xs text-[var(--text3)] uppercase mb-2">Prénom *</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={formData.prenom}
                      onChange={e => handleNameChange('prenom', e.target.value)}
                      onFocus={() => formData.prenom.length >= 2 && setShowSuggestions(prospectSuggestions.length > 0)}
                      data-testid="input-prenom"
                    />
                  </div>
                  <div className="relative">
                    <label className="block text-xs text-[var(--text3)] uppercase mb-2">Nom *</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={formData.nom}
                      onChange={e => handleNameChange('nom', e.target.value)}
                      onFocus={() => formData.nom.length >= 2 && setShowSuggestions(prospectSuggestions.length > 0)}
                      data-testid="input-nom"
                    />
                  </div>
                </div>

                {/* Autocomplete suggestions */}
                {showSuggestions && prospectSuggestions.length > 0 && (
                  <div className="p-2 rounded-lg border border-[var(--accent)] bg-[var(--bg2)]">
                    <div className="text-xs text-[var(--accent)] mb-2">👤 Client(s) déjà connu(s) :</div>
                    {prospectSuggestions.map((p, i) => (
                      <div 
                        key={i}
                        className="p-2 rounded cursor-pointer hover:bg-[var(--bg3)] text-sm"
                        onClick={() => selectProspect(p)}
                      >
                        <strong>{p.prenom} {p.nom}</strong>
                        <span className="text-[var(--text3)] ml-2">{p.telephone} • {p.email}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Téléphone * (+41 XX XXX XX XX)</label>
                  <input
                    type="tel"
                    className="input w-full"
                    placeholder="+41 79 123 45 67"
                    value={formData.telephone}
                    onChange={e => setFormData({ ...formData, telephone: e.target.value })}
                    data-testid="input-telephone"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Email *</label>
                  <input
                    type="email"
                    className="input w-full"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Adresse *</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="Rue, NPA Ville"
                    value={formData.adresse}
                    onChange={e => setFormData({ ...formData, adresse: e.target.value })}
                    data-testid="input-adresse"
                  />
                </div>

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Type d'essai</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type_essai"
                        checked={formData.type_essai === 'walk_in'}
                        onChange={() => setFormData({ ...formData, type_essai: 'walk_in' })}
                      />
                      <span>Walk-in (maintenant)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="type_essai"
                        checked={formData.type_essai === 'rdv_planifie'}
                        onChange={() => setFormData({ ...formData, type_essai: 'rdv_planifie' })}
                      />
                      <span>RDV planifié</span>
                    </label>
                  </div>
                </div>

                {formData.type_essai === 'rdv_planifie' && (
                  <div className="grid-2 gap-4">
                    <div>
                      <label className="block text-xs text-[var(--text3)] uppercase mb-2">Date</label>
                      <input
                        type="date"
                        className="input w-full"
                        value={formData.date_rdv}
                        onChange={e => setFormData({ ...formData, date_rdv: e.target.value })}
                        data-testid="input-date-rdv"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-[var(--text3)] uppercase mb-2">Heure</label>
                      <input
                        type="time"
                        className="input w-full"
                        value={formData.heure_rdv}
                        onChange={e => setFormData({ ...formData, heure_rdv: e.target.value })}
                        data-testid="input-heure-rdv"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Durée prévue</label>
                  <select
                    className="input w-full"
                    value={formData.duree_prevue}
                    onChange={e => setFormData({ ...formData, duree_prevue: parseInt(e.target.value) })}
                    data-testid="select-duree"
                  >
                    {DURATIONS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Step 2: Moto & Permis */}
            {formStep === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">
                    QR Code ou Immatriculation de la moto
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="QR-BMW-R1300GS ou NE 12345"
                      value={formData.bike_code}
                      onChange={e => setFormData({ ...formData, bike_code: e.target.value })}
                      data-testid="input-bike-code"
                    />
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={() => openQRScanner('bike_select')}
                      data-testid="btn-scan-bike"
                    >
                      <ScanLine size={14} /> Scanner
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-ghost"
                      onClick={handleBikeCodeSearch}
                    >
                      <Search size={14} />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">
                    Ou sélectionner dans la flotte *
                  </label>
                  <select
                    className="input w-full"
                    value={formData.bike_id}
                    onChange={e => setFormData({ ...formData, bike_id: e.target.value })}
                    data-testid="select-bike"
                  >
                    <option value="">Sélectionner une moto...</option>
                    {fleet.map(bike => {
                      // Afficher si cette plaque est actuellement en essai (info seulement)
                      const isPlateInUse = stats?.rides_en_cours?.some(r => {
                        const rBike = fleet.find(b => b.id === r.bike_id);
                        return rBike?.immatriculation?.toUpperCase() === bike.immatriculation?.toUpperCase();
                      });
                      
                      // Nombre de personnes en attente pour cette moto
                      const waitingCount = stats?.rides_en_attente?.filter(r => {
                        const rBike = fleet.find(b => b.id === r.bike_id);
                        return rBike?.immatriculation?.toUpperCase() === bike.immatriculation?.toUpperCase();
                      })?.length || 0;
                      
                      return (
                        <option 
                          key={bike.id} 
                          value={bike.id}
                        >
                          {bike.marque} {bike.modele} ({bike.immatriculation}) - {bike.type_moto === 'occasion' ? 'Occasion' : 'Neuf'}
                          {isPlateInUse ? ' 🏍️ En essai' : ''}
                          {waitingCount > 0 ? ` (${waitingCount} en attente)` : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <hr className="border-[var(--border)]" />

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Catégorie Permis *</label>
                  <select
                    className="input w-full"
                    value={formData.categorie_permis}
                    onChange={e => setFormData({ ...formData, categorie_permis: e.target.value })}
                    data-testid="select-categorie-permis"
                  >
                    {PERMIT_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Photo du permis</label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      id="permit-photo"
                      onChange={handlePermitUpload}
                    />
                    <label 
                      htmlFor="permit-photo" 
                      className="btn btn-ghost flex-1 cursor-pointer"
                    >
                      <Camera size={14} /> Photographier
                    </label>
                    <label 
                      htmlFor="permit-photo" 
                      className="btn btn-ghost flex-1 cursor-pointer"
                    >
                      <Upload size={14} /> Importer
                    </label>
                  </div>
                  {formData.photo_permis_path && (
                    <div className="mt-2">
                      <div className="text-xs text-[var(--green)] flex items-center gap-1 mb-2">
                        <Check size={12} /> Photo enregistrée
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost text-xs"
                        onClick={() => setShowPermitImage(true)}
                      >
                        <Eye size={14} /> Voir la photo
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Notes vendeur</label>
                  <textarea
                    className="input w-full"
                    rows={3}
                    placeholder="Budget, projet d'achat, observations..."
                    value={formData.notes_vendeur}
                    onChange={e => setFormData({ ...formData, notes_vendeur: e.target.value })}
                    data-testid="input-notes"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Décharge & Signature */}
            {formStep === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Décharge de responsabilité</label>
                  <div 
                    className="p-4 rounded-lg text-xs text-[var(--text2)] max-h-48 overflow-y-auto whitespace-pre-wrap"
                    style={{ background: 'var(--bg3)' }}
                  >
                    {DECHARGE_TEXT(`${formData.prenom} ${formData.nom}`, formData.categorie_permis)}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Signature électronique *</label>
                  <SignatureCanvas 
                    onSave={handleSignatureSave}
                    onClear={() => setFormData({ ...formData, signature_path: null })}
                  />
                  {formData.signature_path && (
                    <div className="mt-2 text-xs text-[var(--green)] flex items-center gap-1">
                      <Check size={12} /> Signature enregistrée
                    </div>
                  )}
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-[var(--border)] hover:border-[var(--accent)]">
                  <input
                    type="checkbox"
                    checked={formData.decharge_acceptee}
                    onChange={e => setFormData({ ...formData, decharge_acceptee: e.target.checked })}
                    className="mt-1"
                    data-testid="checkbox-decharge"
                  />
                  <span className="text-sm">
                    J'accepte la décharge de responsabilité et certifie que les informations fournies sont exactes. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-[var(--border)] hover:border-[var(--accent)]">
                  <input
                    type="checkbox"
                    checked={formData.consentement_marketing}
                    onChange={e => setFormData({ ...formData, consentement_marketing: e.target.checked })}
                    className="mt-1"
                    data-testid="checkbox-rgpd"
                  />
                  <span className="text-sm text-[var(--text2)]">
                    J'accepte de recevoir des communications marketing de No Name Motorcycles (optionnel - RGPD)
                  </span>
                </label>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between mt-6 pt-4 border-t border-[var(--border)]">
              {formStep > 1 ? (
                <button 
                  type="button" 
                  className="btn btn-ghost"
                  onClick={() => setFormStep(formStep - 1)}
                >
                  <ChevronLeft size={14} /> Précédent
                </button>
              ) : (
                <div />
              )}
              
              {formStep < 3 ? (
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => setFormStep(formStep + 1)}
                  disabled={
                    (formStep === 1 && (!formData.prenom || !formData.nom || !formData.telephone || !formData.email || !formData.adresse)) ||
                    (formStep === 2 && !formData.bike_id)
                  }
                  data-testid="btn-next-step"
                >
                  Suivant <ChevronRight size={14} />
                </button>
              ) : (
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={handleCreateRide}
                  disabled={!formData.decharge_acceptee || !formData.signature_path}
                  data-testid="btn-create-ride"
                >
                  <Check size={14} /> {formData.type_essai === 'walk_in' ? 'Démarrer l\'essai' : 'Planifier le RDV'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* RETURN MODAL */}
      {/* ============================================================ */}
      {showReturnModal && selectedRide && (
        <div className="panel-overlay" onClick={() => setShowReturnModal(false)}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '450px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">Valider le retour</div>
              <button className="panel-close" onClick={() => setShowReturnModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-lg" style={{ background: 'var(--bg3)' }}>
                <div className="text-sm text-[var(--text3)]">Moto partie</div>
                <div className="font-bold text-lg">
                  {getBike(selectedRide.bike_id)?.marque} {getBike(selectedRide.bike_id)?.modele}
                </div>
                <div className="text-sm text-[var(--text2)]">
                  {getBike(selectedRide.bike_id)?.immatriculation}
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">
                  Scanner le QR ou saisir l'immatriculation
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input flex-1"
                    placeholder="QR-BMW-R1300GS ou NE 12345"
                    value={returnScan}
                    onChange={e => { setReturnScan(e.target.value); setReturnError(''); setReturnSuccess(false); }}
                    data-testid="input-return-scan"
                  />
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={() => openQRScanner('return_scan')}
                    data-testid="btn-scan-return"
                  >
                    <ScanLine size={14} /> Scanner
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-ghost"
                    onClick={handleVerifyReturn}
                    data-testid="btn-verify-return"
                  >
                    <Search size={14} />
                  </button>
                </div>
              </div>

              {returnError && (
                <div className="p-3 rounded-lg bg-[var(--red-dim)] text-[var(--red)] text-sm flex items-start gap-2">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  {returnError}
                </div>
              )}

              {returnSuccess && (
                <div className="p-3 rounded-lg bg-[var(--green-dim)] text-[var(--green)] text-sm flex items-center gap-2">
                  <Check size={16} />
                  Moto vérifiée ! Vous pouvez valider le retour.
                </div>
              )}
            </div>

            <button 
              className="btn btn-primary w-full mt-6"
              onClick={handleConfirmReturn}
              disabled={!returnSuccess}
              data-testid="btn-confirm-return"
            >
              <Check size={14} /> Confirmer le retour
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* FLEET MODAL */}
      {/* ============================================================ */}
      {showFleetModal && (
        <div className="panel-overlay" onClick={() => { setShowFleetModal(false); setSelectedBike(null); resetFleetForm(); }}>
          <div className="panel" onClick={e => e.stopPropagation()} style={{ width: '500px' }}>
            <div className="flex justify-between items-start mb-6">
              <div className="panel-title">
                {selectedBike ? 'Modifier la moto' : 'Ajouter une moto'}
              </div>
              <button className="panel-close" onClick={() => { setShowFleetModal(false); setSelectedBike(null); resetFleetForm(); }}>
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Marque *</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="BMW, Yamaha, Honda..."
                    value={fleetForm.marque}
                    onChange={e => setFleetForm({ ...fleetForm, marque: e.target.value })}
                    data-testid="input-fleet-marque"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Modèle *</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="R1300GS, MT-07..."
                    value={fleetForm.modele}
                    onChange={e => setFleetForm({ ...fleetForm, modele: e.target.value })}
                    data-testid="input-fleet-modele"
                  />
                </div>
              </div>

              <div className="grid-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Année *</label>
                  <input
                    type="number"
                    className="input w-full"
                    value={fleetForm.annee}
                    onChange={e => setFleetForm({ ...fleetForm, annee: parseInt(e.target.value) })}
                    data-testid="input-fleet-annee"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Immatriculation *</label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="NE 12345"
                    value={fleetForm.immatriculation}
                    onChange={e => setFleetForm({ ...fleetForm, immatriculation: e.target.value.toUpperCase() })}
                    data-testid="input-fleet-immat"
                  />
                </div>
              </div>

              <div className="grid-2 gap-4">
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Type</label>
                  <select
                    className="input w-full"
                    value={fleetForm.type_moto}
                    onChange={e => setFleetForm({ ...fleetForm, type_moto: e.target.value })}
                    data-testid="select-fleet-type"
                  >
                    <option value="neuf">Neuf</option>
                    <option value="occasion">Occasion</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--text3)] uppercase mb-2">Kilométrage</label>
                  <input
                    type="number"
                    className="input w-full"
                    value={fleetForm.km_actuel}
                    onChange={e => setFleetForm({ ...fleetForm, km_actuel: parseInt(e.target.value) || 0 })}
                    data-testid="input-fleet-km"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Couleur badge</label>
                <div className="flex flex-wrap gap-2">
                  {BADGE_COLORS.map(color => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded-lg border-2 transition-all ${
                        fleetForm.couleur_badge === color.value 
                          ? 'border-white scale-110' 
                          : 'border-transparent'
                      }`}
                      style={{ background: color.value }}
                      onClick={() => setFleetForm({ ...fleetForm, couleur_badge: color.value })}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs text-[var(--text3)] uppercase mb-2">Notes</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  value={fleetForm.notes}
                  onChange={e => setFleetForm({ ...fleetForm, notes: e.target.value })}
                  data-testid="input-fleet-notes"
                />
              </div>

              {/* QR Code preview */}
              {fleetForm.marque && fleetForm.modele && (
                <div className="p-3 rounded-lg" style={{ background: 'var(--bg3)' }}>
                  <div className="text-xs text-[var(--text3)] mb-1">QR Code généré automatiquement</div>
                  <div className="font-mono text-[var(--accent)] font-bold">
                    QR-{fleetForm.marque.toUpperCase().replace(/\s/g, '')}-{fleetForm.modele.toUpperCase().replace(/\s/g, '')}
                  </div>
                </div>
              )}
            </div>

            <button 
              className="btn btn-primary w-full mt-6"
              onClick={selectedBike ? handleUpdateBike : handleCreateBike}
              disabled={!fleetForm.marque || !fleetForm.modele || !fleetForm.immatriculation}
              data-testid="btn-save-bike"
            >
              <Check size={14} /> {selectedBike ? 'Enregistrer' : 'Ajouter à la flotte'}
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* QR SCANNER MODAL */}
      {/* ============================================================ */}
      {showQRScanner && (
        <QRScanner
          onScan={handleQRScan}
          onClose={() => { setShowQRScanner(false); setQrScanContext(null); }}
          placeholder={qrScanContext === 'return_scan' ? "Immatriculation ou QR code..." : "QR-BMW-R1300GS ou NE 12345"}
          showManualInput={true}
        />
      )}

      {/* ============================================================ */}
      {/* PERMIT IMAGE MODAL */}
      {/* ============================================================ */}
      {showPermitImage && formData.photo_permis_path && (
        <div className="panel-overlay" onClick={() => setShowPermitImage(false)}>
          <div className="panel panel-md" onClick={e => e.stopPropagation()}>
            <div className="panel-header">
              <h3>📄 Photo du permis</h3>
              <button className="panel-close" onClick={() => setShowPermitImage(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <img 
                src={`${API_BASE.replace('/api', '')}${formData.photo_permis_path}`}
                alt="Photo du permis"
                className="w-full rounded-lg"
                style={{ maxHeight: '70vh', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRides;
